import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Clock, CheckCircle, XCircle, FileText, TrendingUp, Search, Download, RefreshCw, Check, X, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user, onLogout }) {
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    leave_type: 'all',
    from_date: '',
    to_date: '',
  });
  const [stats, setStats] = useState({
    pending: 0,
    approvedThisMonth: 0,
    denied: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedLeave, setSelectedLeave] = useState(null); // For showing leave details popup

  useEffect(() => {
    fetchLeaves();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, leaves]);

  const fetchLeaves = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leaves`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeaves(response.data);
      setFilteredLeaves(response.data); // Set initial filtered leaves
      calculateStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch leaves');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (leavesData) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const pending = leavesData.filter(l => l.status === 'Pending').length;
    const approvedThisMonth = leavesData.filter(l => {
      const createdDate = new Date(l.created_at);
      return l.status === 'Approved' && 
             createdDate.getMonth() === currentMonth && 
             createdDate.getFullYear() === currentYear;
    }).length;
    const denied = leavesData.filter(l => l.status === 'Denied').length;

    setStats({
      pending,
      approvedThisMonth,
      denied,
      total: leavesData.length,
    });
  };

  const applyFilters = () => {
    let filtered = leaves;

    if (filters.search) {
      filtered = filtered.filter(l => 
        l.user_name.toLowerCase().includes(filters.search.toLowerCase()) ||
        l.user_email.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(l => l.status === filters.status);
    }

    if (filters.leave_type !== 'all') {
      filtered = filtered.filter(l => l.leave_type === filters.leave_type);
    }

    if (filters.from_date) {
      filtered = filtered.filter(l => new Date(l.start_date) >= new Date(filters.from_date));
    }

    if (filters.to_date) {
      filtered = filtered.filter(l => new Date(l.end_date) <= new Date(filters.to_date));
    }

    setFilteredLeaves(filtered);
  };

  const handleStatusUpdate = async (leaveId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/leaves/${leaveId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Leave ${newStatus.toLowerCase()} successfully`);
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update leave');
    }
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'Approved') return 'bg-green-100 text-green-800 border-green-200';
    if (status === 'Pending') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const getLeaveTypeBadge = (type) => {
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const uniqueLeaveTypes = [...new Set(leaves.map(l => l.leave_type))];

  if (loading) {
    return (
      <div className="flex">
        <Sidebar user={user} onLogout={onLogout} />
        <div className="flex-1 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 md:ml-0">
        {/* Header */}
        <div className="bg-white border-b border-border px-4 md:px-8 py-4 md:py-6 pt-16 md:pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Admin Dashboard</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">Manage leave requests and balances</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-4 md:p-8">
          {/* Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
            <div className="stat-card bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-pending">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-muted-foreground">Pending</p>
                  <h3 className="text-2xl md:text-3xl font-bold font-heading text-foreground mt-1 md:mt-2">{stats.pending}</h3>
                </div>
                <div className="w-8 h-8 md:w-12 md:h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-4 h-4 md:w-6 md:h-6 text-yellow-600" />
                </div>
              </div>
              <div className="mt-2 md:mt-4 hidden md:flex items-center text-xs text-green-600">
                <TrendingUp className="w-3 h-3 mr-1" />
                <span>+12%</span>
              </div>
            </div>

            <div className="stat-card bg-white rounded-xl border border-border p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-approved">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-muted-foreground">Approved</p>
                  <h3 className="text-2xl md:text-3xl font-bold font-heading text-foreground mt-1 md:mt-2">{stats.approvedThisMonth}</h3>
                </div>
                <div className="w-8 h-8 md:w-12 md:h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 md:w-6 md:h-6 text-green-600" />
                </div>
              </div>
              <div className="mt-2 md:mt-4 hidden md:flex items-center text-xs text-green-600">
                <TrendingUp className="w-3 h-3 mr-1" />
                <span>+8%</span>
              </div>
            </div>

            <div className="stat-card bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-denied">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-muted-foreground">Denied</p>
                  <h3 className="text-2xl md:text-3xl font-bold font-heading text-foreground mt-1 md:mt-2">{stats.denied}</h3>
                </div>
                <div className="w-8 h-8 md:w-12 md:h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-4 h-4 md:w-6 md:h-6 text-red-600" />
                </div>
              </div>
            </div>

            <div className="stat-card bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-total">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs md:text-sm font-medium text-muted-foreground">Total</p>
                  <h3 className="text-2xl md:text-3xl font-bold font-heading text-foreground mt-1 md:mt-2">{stats.total}</h3>
                </div>
                <div className="w-8 h-8 md:w-12 md:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 md:w-6 md:h-6 text-blue-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-border p-4 md:p-6 mb-4 md:mb-6 shadow-sm">
            <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-3 md:mb-4">Filter Requests</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
              <div className="relative sm:col-span-2 lg:col-span-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search employee..."
                  data-testid="filter-search"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-3 py-2 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <select
                data-testid="filter-status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-3 py-2 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Status</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Denied">Denied</option>
              </select>

              <select
                data-testid="filter-leave-type"
                value={filters.leave_type}
                onChange={(e) => setFilters({ ...filters, leave_type: e.target.value })}
                className="px-3 py-2 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Leave Types</option>
                {uniqueLeaveTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              <input
                type="date"
                placeholder="From Date"
                data-testid="filter-from-date"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                className="px-3 py-2 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />

              <input
                type="date"
                placeholder="To Date"
                data-testid="filter-to-date"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                className="px-3 py-2 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex gap-2 mt-3 md:mt-4">
              <button
                onClick={() => setFilters({ search: '', status: 'all', leave_type: 'all', from_date: '', to_date: '' })}
                data-testid="clear-filters"
                className="px-3 md:px-4 py-2 text-xs md:text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                × Clear All
              </button>
            </div>
          </div>

          {/* Leave Requests - Mobile Cards / Desktop Table */}
          <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-border gap-2">
              <div>
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">Leave Requests</h3>
                <p className="text-xs md:text-sm text-muted-foreground">{filteredLeaves.length} of {leaves.length} requests</p>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={fetchLeaves}
                  data-testid="refresh-button"
                  className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 text-xs md:text-sm font-medium text-foreground bg-white border border-input rounded-lg hover:bg-muted transition-colors"
                >
                  <RefreshCw className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="block md:hidden">
              {filteredLeaves.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  No leave requests found
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredLeaves.map((leave) => (
                    <div key={leave.leave_id} className="p-4" data-testid={`leave-card-${leave.leave_id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="font-medium text-sm text-foreground">{leave.user_name}</p>
                            <p className="text-xs text-muted-foreground">{leave.user_email}</p>
                          </div>
                          {/* Info button with notification dot */}
                          <button
                            onClick={() => setSelectedLeave(leave)}
                            className="relative p-1.5 hover:bg-muted rounded-lg"
                          >
                            <MessageCircle className="w-4 h-4 text-muted-foreground" />
                            <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${
                              leave.half_day_exceed_warning ? 'bg-red-500' : 'bg-green-500'
                            }`} />
                          </button>
                        </div>
                        <span className={`status-badge border text-xs ${getStatusBadgeClass(leave.status)}`}>
                          {leave.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div>
                          <span className="text-muted-foreground">Type: </span>
                          <span className="font-medium">{leave.leave_type}</span>
                          {leave.auto_converted_to_unpaid && <span className="text-orange-600 ml-1">⚡</span>}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Days: </span>
                          <span className="font-medium">{leave.days_off}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Dates: </span>
                          <span className="font-medium">
                            {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      {leave.status === 'Pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleStatusUpdate(leave.leave_id, 'Approved')}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition-colors"
                          >
                            <Check className="w-3 h-3" /> Approve
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(leave.leave_id, 'Denied')}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors"
                          >
                            <X className="w-3 h-3" /> Deny
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full" data-testid="leave-requests-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Dates</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Leave Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Info</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Submitted</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-border">
                  {filteredLeaves.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-muted-foreground">
                        No leave requests found
                      </td>
                    </tr>
                  ) : (
                    filteredLeaves.map((leave) => (
                      <tr key={leave.leave_id} className="hover:bg-muted/50 transition-colors" data-testid={`leave-row-${leave.leave_id}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-primary font-semibold text-sm">
                                {leave.user_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-sm text-foreground">{leave.user_name}</p>
                              <p className="text-xs text-muted-foreground">{leave.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {new Date(leave.start_date).toLocaleDateString()} to {new Date(leave.end_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1">
                            <span className={`status-badge border ${getLeaveTypeBadge(leave.leave_type)}`}>
                              {leave.leave_type}
                            </span>
                            {leave.auto_converted_to_unpaid && (
                              <span className="text-xs text-orange-600" title="Auto-converted from paid leave">⚡</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground font-medium">
                          {leave.days_off} days
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => setSelectedLeave(leave)}
                            className="relative p-2 hover:bg-muted rounded-lg transition-colors"
                            title="View details"
                          >
                            <MessageCircle className="w-5 h-5 text-muted-foreground" />
                            {/* Notification dot */}
                            <span className={`absolute top-1 right-1 w-2.5 h-2.5 rounded-full ${
                              leave.half_day_exceed_warning ? 'bg-red-500' : 'bg-green-500'
                            }`} />
                          </button>
                        </td>
                        <td className="px-6 py-4 text-xs text-muted-foreground">
                          {new Date(leave.created_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`status-badge border ${getStatusBadgeClass(leave.status)}`} data-testid={`status-${leave.leave_id}`}>
                            {leave.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {leave.status === 'Pending' ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleStatusUpdate(leave.leave_id, 'Approved')}
                                data-testid={`approve-${leave.leave_id}`}
                                className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                                title="Approve"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleStatusUpdate(leave.leave_id, 'Denied')}
                                data-testid={`deny-${leave.leave_id}`}
                                className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                                title="Deny"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Leave Details Popup */}
      {selectedLeave && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedLeave(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-heading font-semibold">Leave Details</h2>
              </div>
              <button onClick={() => setSelectedLeave(null)} className="p-1 hover:bg-muted rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Warning Notification */}
              {selectedLeave.half_day_exceed_warning && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="text-sm font-medium">Half Day Limit Exceeded</span>
                  </div>
                  <p className="text-xs text-red-700 mt-1">
                    This employee has exceeded the half day threshold for this year.
                  </p>
                </div>
              )}

              {/* Auto Conversion Notice */}
              {selectedLeave.auto_converted_to_unpaid && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center gap-2 text-orange-800">
                    <span className="text-sm font-medium">⚡ Auto-converted to Unpaid</span>
                  </div>
                  <p className="text-xs text-orange-700 mt-1">
                    Original request was for paid leave but was converted due to insufficient balance.
                  </p>
                </div>
              )}
              
              {/* Leave Reason */}
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-800 mb-1">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-medium">Leave Reason</span>
                </div>
                <p className="text-sm text-green-700">
                  {selectedLeave.leave_reason || 'No reason provided'}
                </p>
              </div>

              {/* Leave Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Employee:</span>
                  <p className="font-medium">{selectedLeave.user_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Leave Type:</span>
                  <p className="font-medium">{selectedLeave.leave_type}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Duration:</span>
                  <p className="font-medium">{selectedLeave.days_off} days</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <p className="font-medium">{selectedLeave.status}</p>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setSelectedLeave(null)}
              className="w-full mt-4 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/70 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
